import React, { useEffect } from "react";
import {
  ScrollView,
  View,
  Platform,
  StyleSheet,
  Image,
  Pressable,
} from "react-native";

import { Text, Card, IconButton } from "react-native-paper";

import { useTheme } from "../context/ThemeContext";
import { ThirdPartyCredits } from "./ThirdPartyCredits";

interface LegalPageProps {
  type: "terms" | "privacy" | "dmca" | "about" | "contact";
  onBack?: () => void;
}

export const LegalPage: React.FC<LegalPageProps> = ({ type, onBack }) => {
  const { theme } = useTheme();

  // Set page title for web
  useEffect(() => {
    if (Platform.OS === "web" && typeof document !== "undefined") {
      const titles = {
        terms: "Terms of Service - Synapse",
        privacy: "Privacy Policy - Synapse",
        dmca: "DMCA Policy - Synapse",
        about: "About - Synapse",
        contact: "Contact - Synapse",
      };
      document.title = titles[type];
    }
  }, [type]);

  const LegalPageHeader = () => (
    <View style={styles.headerContainer}>
      <Pressable
        style={styles.headerContent}
        onPress={() => (window.location.href = "/")}
      >
        <Image
          // eslint-disable-next-line @typescript-eslint/no-require-imports
          source={require("../../assets/favicon.png")}
          style={styles.logo}
        />
        <Text style={[styles.title, { color: theme.colors.onSurface }]}>
          Synapse
        </Text>
      </Pressable>
    </View>
  );

  // On web the legal pages open in their own tab, so "back" goes to the game.
  const goBack = () => {
    if (onBack) onBack();
    else if (Platform.OS === "web" && typeof window !== "undefined") {
      window.location.href = "/";
    }
  };

  const renderBackButton = () =>
    (Platform.OS === "web" || onBack) && (
      <Pressable
        onPress={goBack}
        accessibilityRole="link"
        accessibilityLabel="Back to game"
        style={styles.backButton}
      >
        <IconButton
          icon="arrow-left"
          size={20}
          iconColor={theme.colors.primary}
          style={styles.backIcon}
        />
        <Text style={{ color: theme.colors.primary, fontSize: 16 }}>
          Back to game
        </Text>
      </Pressable>
    );

  const renderDates = (effective: string | null, updated: string) => (
    <View style={{ marginBottom: 20 }}>
      {effective && (
        <Text style={{ color: theme.colors.onSurfaceVariant }}>
          Effective date: {effective}
        </Text>
      )}
      <Text style={{ color: theme.colors.onSurfaceVariant }}>
        Last updated: {updated}
      </Text>
    </View>
  );

  const renderTermsOfService = () => (
    <ScrollView style={{ flex: 1, backgroundColor: theme.colors.background }}>
      <LegalPageHeader />
      <View style={styles.page}>
        {renderBackButton()}
        <Text
          variant="headlineLarge"
          style={{ color: theme.colors.onBackground, marginBottom: 20 }}
        >
          Terms of Service
        </Text>

        {renderDates("January 10, 2025", "October 5, 2026")}

        <Card
          style={{ marginBottom: 20, backgroundColor: theme.colors.surface }}
        >
          <Card.Content>
            <Text
              variant="headlineSmall"
              style={{ color: theme.colors.onSurface, marginBottom: 10 }}
            >
              1. Acceptance of Terms
            </Text>
            <Text style={{ color: theme.colors.onSurface, lineHeight: 24 }}>
              By accessing or using Synapse ("the Service"), a word navigation
              puzzle game operated by Neumann's Workshop, LLC, doing business as
              Galaxy Brain Entertainment ("Company", "we", "us", or "our"), you
              agree to be bound by these Terms of Service ("Terms"). If you do
              not agree to these Terms, do not use the Service.
            </Text>
          </Card.Content>
        </Card>

        <Card
          style={{ marginBottom: 20, backgroundColor: theme.colors.surface }}
        >
          <Card.Content>
            <Text
              variant="headlineSmall"
              style={{ color: theme.colors.onSurface, marginBottom: 10 }}
            >
              2. Description of Service
            </Text>
            <Text
              style={{
                color: theme.colors.onSurface,
                lineHeight: 24,
                marginBottom: 10,
              }}
            >
              Synapse is an interactive word navigation puzzle game that
              challenges players to find paths between words using semantic
              relationships. The Service includes:
            </Text>
            <Text style={{ color: theme.colors.onSurface, lineHeight: 24 }}>
              • Daily word navigation challenges{"\n"}• Progress tracking and
              statistics
            </Text>
          </Card.Content>
        </Card>

        <Card
          style={{ marginBottom: 20, backgroundColor: theme.colors.surface }}
        >
          <Card.Content>
            <Text
              variant="headlineSmall"
              style={{ color: theme.colors.onSurface, marginBottom: 10 }}
            >
              3. Monetization
            </Text>
            <Text
              style={{
                color: theme.colors.onSurface,
                lineHeight: 24,
                marginBottom: 10,
              }}
            >
              • Synapse is free to play.
            </Text>
          </Card.Content>
        </Card>

        <Card
          style={{ marginBottom: 20, backgroundColor: theme.colors.surface }}
        >
          <Card.Content>
            <Text
              variant="headlineSmall"
              style={{ color: theme.colors.onSurface, marginBottom: 10 }}
            >
              4. Data and Privacy
            </Text>
            <Text style={{ color: theme.colors.onSurface, lineHeight: 24 }}>
              <Text style={{ fontWeight: "bold" }}>Local Data Storage:</Text>
              {"\n"}• All game progress and settings are stored locally on your
              device.{"\n"}• We do not collect or store any personal data on our
              servers.{"\n\n"}
              <Text style={{ fontWeight: "bold" }}>Data Control:</Text>
              {"\n"}• You can clear your browser cache at any time to reset your
              game progress.{"\n"}• Deleting the app will permanently remove all
              of your data.
            </Text>
          </Card.Content>
        </Card>

        <Card
          style={{ marginBottom: 20, backgroundColor: theme.colors.surface }}
        >
          <Card.Content>
            <Text
              variant="headlineSmall"
              style={{ color: theme.colors.onSurface, marginBottom: 10 }}
            >
              5. Contact Information
            </Text>
            <Text style={{ color: theme.colors.onSurface, lineHeight: 24 }}>
              For questions about these Terms or the Service:{"\n"}
              Support: social@neumannsworkshop.com{"\n"}
              Website: synapse.gbe.games
            </Text>
          </Card.Content>
        </Card>

        <Text
          style={{
            color: theme.colors.onSurfaceVariant,
            textAlign: "center",
            marginTop: 20,
          }}
        >
          These Terms of Service are designed to protect both users and the
          service while maintaining a fair and enjoyable gaming experience.
        </Text>
      </View>
    </ScrollView>
  );

  const renderPrivacyPolicy = () => (
    <ScrollView style={{ flex: 1, backgroundColor: theme.colors.background }}>
      <LegalPageHeader />
      <View style={styles.page}>
        {renderBackButton()}
        <Text
          variant="headlineLarge"
          style={{ color: theme.colors.onBackground, marginBottom: 20 }}
        >
          Privacy Policy
        </Text>

        {renderDates(null, "August 18, 2025")}

        <Card
          style={{ marginBottom: 20, backgroundColor: theme.colors.surface }}
        >
          <Card.Content>
            <Text
              variant="headlineSmall"
              style={{ color: theme.colors.onSurface, marginBottom: 10 }}
            >
              Introduction
            </Text>
            <Text style={{ color: theme.colors.onSurface, lineHeight: 24 }}>
              Synapse ("we," "our," or "us") respects your privacy and is
              committed to protecting your personal information. This Privacy
              Policy explains how we collect, use, disclose, and safeguard your
              information when you use our word navigation puzzle game.
            </Text>
          </Card.Content>
        </Card>

        <Card
          style={{ marginBottom: 20, backgroundColor: theme.colors.surface }}
        >
          <Card.Content>
            <Text
              variant="headlineSmall"
              style={{ color: theme.colors.onSurface, marginBottom: 10 }}
            >
              Information We Collect
            </Text>
            <Text style={{ color: theme.colors.onSurface, lineHeight: 24 }}>
              • Game Progress: Game statistics, achievements, and challenge
              completions are stored locally on your device.{"\n"}• We do not
              collect any personally identifiable information.
            </Text>
          </Card.Content>
        </Card>

        <Card
          style={{ marginBottom: 20, backgroundColor: theme.colors.surface }}
        >
          <Card.Content>
            <Text
              variant="headlineSmall"
              style={{ color: theme.colors.onSurface, marginBottom: 10 }}
            >
              How We Use Your Information
            </Text>
            <Text style={{ color: theme.colors.onSurface, lineHeight: 24 }}>
              We do not collect or use any of your personal information. All
              game data is stored locally on your device and is not transmitted
              to our servers.
            </Text>
          </Card.Content>
        </Card>

        <Card
          style={{ marginBottom: 20, backgroundColor: theme.colors.surface }}
        >
          <Card.Content>
            <Text
              variant="headlineSmall"
              style={{ color: theme.colors.onSurface, marginBottom: 10 }}
            >
              Your Rights & Controls
            </Text>
            <Text
              style={{
                color: theme.colors.onSurface,
                lineHeight: 24,
                marginBottom: 12,
              }}
            >
              You have full control over your data:
            </Text>
            <Text style={{ color: theme.colors.onSurface, lineHeight: 24 }}>
              <Text style={{ fontWeight: "bold" }}>Data Management:</Text>
              {"\n"}• You can clear your browser cache to reset all of your game
              data at any time.{"\n"}• Deleting the application from your device
              will permanently remove all associated data.
            </Text>
          </Card.Content>
        </Card>

        <Card
          style={{ marginBottom: 20, backgroundColor: theme.colors.surface }}
        >
          <Card.Content>
            <Text
              variant="headlineSmall"
              style={{ color: theme.colors.onSurface, marginBottom: 10 }}
            >
              Contact Us
            </Text>
            <Text style={{ color: theme.colors.onSurface, lineHeight: 24 }}>
              If you have questions about this Privacy Policy, please contact us
              at:{"\n"}
              Email: social@neumannsworkshop.com{"\n"}
              Website: synapse.gbe.games
            </Text>
          </Card.Content>
        </Card>
      </View>
    </ScrollView>
  );

  const renderDMCAPolicy = () => (
    <ScrollView style={{ flex: 1, backgroundColor: theme.colors.background }}>
      <LegalPageHeader />
      <View style={styles.page}>
        {renderBackButton()}
        <Text
          variant="headlineLarge"
          style={{ color: theme.colors.onBackground, marginBottom: 20 }}
        >
          DMCA Notice & Takedown Policy
        </Text>

        {renderDates("January 10, 2025", "October 5, 2026")}

        <Card
          style={{ marginBottom: 20, backgroundColor: theme.colors.surface }}
        >
          <Card.Content>
            <Text
              variant="headlineSmall"
              style={{ color: theme.colors.onSurface, marginBottom: 10 }}
            >
              DMCA Compliance
            </Text>
            <Text style={{ color: theme.colors.onSurface, lineHeight: 24 }}>
              Galaxy Brain Entertainment (operating under Neumann's Workshop,
              LLC) respects the intellectual property rights of others and
              expects users to do the same. In accordance with the Digital
              Millennium Copyright Act of 1998 (DMCA), we will respond promptly
              to valid notices of alleged copyright infringement.
            </Text>
          </Card.Content>
        </Card>

        <Card
          style={{ marginBottom: 20, backgroundColor: theme.colors.surface }}
        >
          <Card.Content>
            <Text
              variant="headlineSmall"
              style={{ color: theme.colors.onSurface, marginBottom: 10 }}
            >
              Filing a DMCA Notice
            </Text>
            <Text style={{ color: theme.colors.onSurface, lineHeight: 24 }}>
              If you believe that your copyrighted work has been used in a way
              that constitutes copyright infringement in the Synapse game or
              website, please provide our designated Copyright Agent with the
              required information including identification of the copyrighted
              work, your contact information, and a statement of good faith
              belief.
            </Text>
          </Card.Content>
        </Card>

        <Card
          style={{ marginBottom: 20, backgroundColor: theme.colors.surface }}
        >
          <Card.Content>
            <Text
              variant="headlineSmall"
              style={{ color: theme.colors.onSurface, marginBottom: 10 }}
            >
              DMCA Contact Information
            </Text>
            <Text style={{ color: theme.colors.onSurface, lineHeight: 24 }}>
              Copyright Agent: Galaxy Brain Entertainment{"\n"}
              DMCA Compliance{"\n"}
              Email: social@neumannsworkshop.com{"\n"}
              Subject Line: "DMCA Takedown Notice"
            </Text>
          </Card.Content>
        </Card>

        <Card
          style={{ marginBottom: 20, backgroundColor: theme.colors.surface }}
        >
          <Card.Content>
            <Text
              variant="headlineSmall"
              style={{ color: theme.colors.onSurface, marginBottom: 10 }}
            >
              Word Data Sources
            </Text>
            <Text style={{ color: theme.colors.onSurface, lineHeight: 24 }}>
              Synapse's word connections come from a word2vec embedding we
              trained on openly licensed text (the Common Pile), with the
              vocabulary curated from open word lists. No third-party AI model
              generates them. Each source and its licence is listed under Data
              Credits on the About page.
            </Text>
          </Card.Content>
        </Card>
      </View>
    </ScrollView>
  );

  const renderAboutPage = () => (
    <ScrollView style={{ flex: 1, backgroundColor: theme.colors.background }}>
      <LegalPageHeader />
      <View style={styles.page}>
        {renderBackButton()}
        <Text
          variant="headlineLarge"
          style={{ color: theme.colors.onBackground, marginBottom: 20 }}
        >
          About Synapse
        </Text>

        <Card
          style={{ marginBottom: 20, backgroundColor: theme.colors.surface }}
        >
          <Card.Content>
            <Text
              variant="headlineSmall"
              style={{ color: theme.colors.onSurface, marginBottom: 10 }}
            >
              About the Game
            </Text>
            <Text style={{ color: theme.colors.onSurface, lineHeight: 24 }}>
              Synapse™ is a word navigation puzzle game that challenges players
              to find paths between words using semantic relationships. Navigate
              through a web of interconnected concepts to reach your target
              word.
            </Text>
          </Card.Content>
        </Card>

        <Card
          style={{ marginBottom: 20, backgroundColor: theme.colors.surface }}
        >
          <Card.Content>
            <Text
              variant="headlineSmall"
              style={{ color: theme.colors.onSurface, marginBottom: 10 }}
            >
              Business Information
            </Text>
            <Text style={{ color: theme.colors.onSurface, lineHeight: 24 }}>
              Synapse™ is developed by Galaxy Brain Entertainment, operating
              under Neumann's Workshop, LLC.{"\n\n"}
              This is a free web application that stores your game progress
              locally in your browser cache.{"\n\n"}
              Galaxy Brain Entertainment{"\n"}A division of Neumann's Workshop,
              LLC{"\n\n"}
              Contact: social@neumannsworkshop.com
            </Text>
          </Card.Content>
        </Card>

        <ThirdPartyCredits />

        <Card
          style={{ marginBottom: 20, backgroundColor: theme.colors.surface }}
        >
          <Card.Content>
            <Text
              variant="headlineSmall"
              style={{ color: theme.colors.onSurface, marginBottom: 10 }}
            >
              Legal Information
            </Text>
            <Text style={{ color: theme.colors.onSurface, lineHeight: 24 }}>
              © 2025–2026 Galaxy Brain Entertainment (Neumann's Workshop, LLC).
              All rights reserved.{"\n"}
              Synapse™ is a trademark of Galaxy Brain Entertainment.
            </Text>
          </Card.Content>
        </Card>
      </View>
    </ScrollView>
  );

  const renderContactPage = () => (
    <ScrollView style={{ flex: 1, backgroundColor: theme.colors.background }}>
      <LegalPageHeader />
      <View style={styles.page}>
        {renderBackButton()}
        <Text
          variant="headlineLarge"
          style={{ color: theme.colors.onBackground, marginBottom: 20 }}
        >
          Contact Us
        </Text>

        <Card
          style={{ marginBottom: 20, backgroundColor: theme.colors.surface }}
        >
          <Card.Content>
            <Text
              variant="headlineSmall"
              style={{ color: theme.colors.onSurface, marginBottom: 10 }}
            >
              General Support
            </Text>
            <Text style={{ color: theme.colors.onSurface, lineHeight: 24 }}>
              For questions, support, or feedback about Synapse:{"\n\n"}
              Email: social@neumannsworkshop.com{"\n"}
              Website: synapse.gbe.games
            </Text>
          </Card.Content>
        </Card>

        <Card
          style={{ marginBottom: 20, backgroundColor: theme.colors.surface }}
        >
          <Card.Content>
            <Text
              variant="headlineSmall"
              style={{ color: theme.colors.onSurface, marginBottom: 10 }}
            >
              Privacy & Data Requests
            </Text>
            <Text style={{ color: theme.colors.onSurface, lineHeight: 24 }}>
              For privacy-related questions about Synapse:
              {"\n\n"}
              Email: social@neumannsworkshop.com{"\n"}
              Subject: "Privacy Request" or "Data Request"{"\n\n"}
              <Text style={{ fontWeight: "bold" }}>We can help with:</Text>
              {"\n"}• Questions about local data storage{"\n"}• Technical issues
              with browser cache
              {"\n"}• Privacy compliance questions
            </Text>
          </Card.Content>
        </Card>

        <Card
          style={{ marginBottom: 20, backgroundColor: theme.colors.surface }}
        >
          <Card.Content>
            <Text
              variant="headlineSmall"
              style={{ color: theme.colors.onSurface, marginBottom: 10 }}
            >
              Legal & Compliance
            </Text>
            <Text style={{ color: theme.colors.onSurface, lineHeight: 24 }}>
              For legal matters, DMCA notices, or compliance issues:{"\n\n"}
              Email: social@neumannsworkshop.com{"\n"}
              Subject: "Legal Matter" or "DMCA Notice"
            </Text>
          </Card.Content>
        </Card>

        <Card
          style={{ marginBottom: 20, backgroundColor: theme.colors.surface }}
        >
          <Card.Content>
            <Text
              variant="headlineSmall"
              style={{ color: theme.colors.onSurface, marginBottom: 10 }}
            >
              Business Information
            </Text>
            <Text style={{ color: theme.colors.onSurface, lineHeight: 24 }}>
              Galaxy Brain Entertainment{"\n"}A division of Neumann's Workshop,
              LLC{"\n\n"}
              We aim to respond to all inquiries within 48 hours during business
              days.
            </Text>
          </Card.Content>
        </Card>
      </View>
    </ScrollView>
  );

  switch (type) {
    case "terms":
      return renderTermsOfService();
    case "privacy":
      return renderPrivacyPolicy();
    case "dmca":
      return renderDMCAPolicy();
    case "about":
      return renderAboutPage();
    case "contact":
      return renderContactPage();
    default:
      return null;
  }
};

const styles = StyleSheet.create({
  page: {
    padding: 20,
    maxWidth: 800,
    width: "100%",
    alignSelf: "center",
  },
  backButton: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    minHeight: 44,
    paddingRight: 12,
    marginBottom: 12,
    marginLeft: -8,
  },
  backIcon: {
    margin: 0,
  },
  headerContainer: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(255, 255, 255, 0.1)",
    alignItems: "center",
  },
  headerContent: {
    flexDirection: "row",
    alignItems: "center",
    maxWidth: 800,
    width: "100%",
  },
  logo: {
    width: 32,
    height: 32,
    marginRight: 12,
  },
  title: {
    fontWeight: "bold",
    fontSize: 20,
  },
});
